import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import Button from '../../components/Button/Button';
import Typography from '../../components/Typography';
import { theme } from '../../theme';
import { getPlatform, getIcon } from '../../utils/socialMediaUtils';
import { getVehicleIcon, getDefaultIconProps } from '../../utils/vehicleIcons';
import { useTranslation } from 'react-i18next';

const UserInfoSection = ({ profileUser, userId, isOwnProfile }) => {
  const { t } = useTranslation('userProfile');
  const navigate = useNavigate();

  return (
    <UserInfo>
      <Bio>
        <Typography $variant="title-4-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold" as="h3">
          {/* Bio */}
          {t('profile.title')}
        </Typography>
        {!profileUser.description ? (
          isOwnProfile ? (
            <EmptyStateWrapper>
              <Typography $variant="body-2-regular" color={theme.colors.defaultWeak}>
                {t('profile.emptyState.noBio')}
                {/* Añade una biografía para que otros miembros de la comunidad te conozcan mejor. */}
              </Typography>
              <Button
                $variant="outline"
                // onClick={() => navigate(`/user/${userId}/edit-profile`, { state: { returnTo: `/user/${userId}` } })}
                onClick={() => navigate(`/edit-profile`, { state: { returnTo: `/user/${userId}` } })}

              >
                {/* Editar perfil */}
                {t('profile.edit')}
              </Button>
            </EmptyStateWrapper>
          ) : (
            <Typography $variant="body-2-regular" color={theme.colors.defaultWeak}>
              {/* Este usuario no tiene Bio */}
              {t('profile.emptyState.noBioPublicMsg')}
            </Typography>
          )
        ) : (
          <Typography $variant="body-2-regular" color={theme.colors.defaultWeak}>
            {profileUser.description}
          </Typography>
        )}
        {profileUser.socialMediaLinks && profileUser.socialMediaLinks.length > 0 && (
          <SocialMediaList>
            {profileUser.socialMediaLinks.map((socialMediaLink, index) => {
              const platform = getPlatform(socialMediaLink.url);
              const IconComponent = getIcon(platform);

              return (
                <SocialMediaItem key={index}>
                  <IconLink href={socialMediaLink.url} target='_blank' rel='noreferrer'>
                    {/* <img src={icon} alt={platform} /> */}
                    <IconComponent fill={theme.colors.defaultWeak} />
                  </IconLink>
                </SocialMediaItem>
              );
            })}
          </SocialMediaList>
        )}
      </Bio>
      <Vehicles>
        {profileUser.vehicles && profileUser.vehicles.length > 0 ? (
          <>
            <VehiclesHeader>
              <Typography $variant="title-4-semibold" as="h3">
                {t('garage.titlePublicMsg')}
              </Typography>
              <Link to={`/user/${userId}/garage`}>
                <Typography $variant="body-1-semibold" as="p" color={theme.colors.defaultStrong}>
                  {/* Ver más */}
                  {t('garage.viewGarage')}
                </Typography>
              </Link>
            </VehiclesHeader>
            <VehicleList>
              {profileUser.vehicles.map(vehicle => {
                const VehicleIconComponent = getVehicleIcon(vehicle?.vehicleType);
                const iconProps = { ...getDefaultIconProps(theme.fill.brandMain), width: 40, height: 40 };
                return (
                  <Vehicle key={vehicle.id}>
                    <VehicleContent>
                      {vehicle?.image ? (
                        <VehicleImage src={vehicle.image} alt={vehicle.brand + ' ' + vehicle.model} />
                      ) : (
                        <VehicleIconSquare>
                          <VehicleIconComponent {...iconProps} />
                        </VehicleIconSquare>
                      )}
                      <VehicleData>
                        {vehicle.nickname ? (
                          <>
                            <Typography $variant="body-1-medium" color={theme.colors.defaultMain}>
                              {vehicle?.brand}
                              <Typography as="span" color={theme.colors.defaultStrong} style={{ marginLeft: '4px' }}>
                                {vehicle?.model}
                              </Typography>
                            </Typography>
                            <Typography $variant="title-5-semibold" color={theme.colors.defaultMain}>
                              {vehicle.nickname}
                            </Typography>
                            <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                              {vehicle.year}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography $variant="body-1-medium" color={theme.colors.defaultMain}>
                              {vehicle?.brand}
                            </Typography>
                            <Typography $variant="title-5-semibold" color={theme.colors.defaultMain}>
                              {vehicle.model}
                            </Typography>
                            <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                              {vehicle.year}
                            </Typography>
                          </>
                        )}
                      </VehicleData>
                    </VehicleContent>
                  </Vehicle>
                );
              })}
            </VehicleList>
          </>
        ) : (
          // Empty state para vehículos: Si es el propio perfil, mostrar botón para añadir vehículo
          isOwnProfile ? (
            <>
              <Typography $variant="title-4-semibold" as="h3">Garaje</Typography>
              <EmptyStateWrapper>
                <Typography $variant="body-2-regular" color={theme.colors.defaultWeak}>
                  {/* Añade tus vehiculos a tu garaje y haz gala de ellos. Al inscribirte en un evento podrás elegir con cuál asistir. */}
                  {t('garage.emptyState.noVehicles')}
                </Typography>
                <Button $variant="outline" onClick={() => navigate(`/user/${userId}/garage`)}>
                  {/* Añadir vehículo */}
                  {t('garage.button')}
                </Button>
              </EmptyStateWrapper>
            </>
          ) : (
            <>
              <Typography $variant="title-4-semibold" as="h3">Garaje</Typography>
              <Typography $variant="body-2-regular" color={theme.colors.defaultWeak}>
                {/* Sin Vehículos */}
                {t('garage.emptyState.noVehiclesPublicMsg')}
              </Typography>
            </>
          )
        )}
      </Vehicles>
      <Achievements>
        <Typography $variant="title-4-semibold" as="h3">
          {t('achievements.title')}
          {/* Logros */}
        </Typography>
        <Typography $variant="body-2-regular" color={theme.colors.defaultWeak}>
          {/* Proximamente... */}
          {t('achievements.emptyState.comingSoon')}
        </Typography>
      </Achievements>
    </UserInfo>
  );
};

export default UserInfoSection;

const UserInfo = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  display: flex;
  flex-direction: column;
  grid-area: 1 / 1 / 2 / 5;
  height: fit-content;
`;

const UserInfoBlock = styled.div`
  align-items: flex-start;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.sm};
  padding-top: 12px;
`;

const Bio = styled(UserInfoBlock)`
`;

const SocialMediaList = styled.ul`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
  justify-content: center;
  list-style: none;
  margin-top:  ${({ theme }) => theme.sizing.xs};
  padding: 0;
`;

const SocialMediaItem = styled.li`
`;

const IconLink = styled.a`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  display: flex;
  opacity: 0.8;
  padding: ${({ theme }) => theme.sizing.xs};
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
    border: 1px solid ${({ theme }) => theme.border.defaultWeak};
    opacity: 1;
  }
`;

const Vehicles = styled(UserInfoBlock)`
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const VehicleList = styled.ul`
  align-items: flex-start;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: ${({ theme }) => theme.sizing.xxs};
`;

const Vehicle = styled.li`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-right: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const VehicleContent = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  gap: 16px;
`;

const VehicleImage = styled.img`
  border-radius: ${({ theme }) => theme.radius.xxs};
  height: auto;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  width: 80px;
`;

const VehicleIconSquare = styled.div`
  min-width: 80px;
  height: auto;
  aspect-ratio: 4 / 3;
  border-radius: ${({ theme }) => theme.radius.xxs};
  background: ${({ theme }) => theme.fill.inverseWeak};
  display: flex;
  align-items: center;
  justify-content: center;
  // border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const VehicleData = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const VehiclesHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const Achievements = styled(UserInfoBlock)`
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const EmptyStateWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
`;
